import React, { Component, useContext, useEffect, useRef, useState } from "react";
import "./OwDataMapper.scss";
import IDataMapperSaveItem from "../../../models/IDataMapperSaveItem";
import Modal, { Styles } from "react-modal";
import ISupplierModel from "../../../models/ISupplierModel";
import OwButton from "../OwButton/OwButton";
import OwStaticTypeahead from "../OwStaticTypeahead/OwStaticTypeahead";
import OwTitle from "../OwTitle/OwTitle";
import * as _ from "lodash-es";
import SupplierContext from "../../../contexts/SupplierContext";

interface IDataMapperProcessedItem {
    staticValue: any;
    staticTitle: string;
    staticSubTitle: string;
    selectedOption: any;
}

type Props = {
    isVisible: boolean;

    saveFn: (items: IDataMapperSaveItem[]) => void;
    cancelFn: () => void;

    mainTitle: string;

    // These are the left hand side (static/view only)
    staticTitle: string;
    staticOptions: any[];
    staticValueProp: string;
    staticTitleProp: string;
    staticSubTitleProp?: string;

    staticValueSelectedProp?: string; // If the property that the selected value should compare against is differen than the static value prop
    staticValueSelectedSubProp?: string; // If the property that the selected value should compare against is differen than the static value prop

    // This is the side with the options that are used to search against
    // and match up with the left side
    selectableTitle: string;
    selectableOptions: any[];
    selectableValueProp: string;
    selectableTitleProp: string;
    selectableSubTitleProp?: string;

    selectedValueProp?: string; // If the selected value property off of the selected value is different on the static object than selectableValueProp
};

export const OwDataMapper = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const [processedStaticOptions, setProcessedStaticOptions] = useState<IDataMapperProcessedItem[]>([]);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const customStyles: Styles = {
        overlay: {
            zIndex: 10,
        },
        content: {
            width: "50rem",
            height: "40rem",
            padding: "1rem",
        },
    };

    const isVendorNameOverride: boolean = (supplierModel && !_.isNil(supplierModel.vendor_name_override)) || false;

    useEffect(() => {
        if (!processedStaticOptions.length && !isLoaded) {
            processItems();
        }
    }, []);

    const getStaticValue = (staticOption: any) => {
        if (props.staticValueSelectedProp && staticOption[props.staticValueSelectedProp]) {
            if (!props.staticValueSelectedSubProp) {
                return staticOption[props.staticValueSelectedProp];
            } else {
                return staticOption[props.staticValueProp][props.staticValueSelectedSubProp];
            }
        } else {
            return staticOption[props.staticValueProp];
        }
    };

    const processItems = () => {
        const _processedStaticOptions = props.staticOptions.map((staticOption: any) => {
            // TODO Something just doesn't seem right with this....
            const staticValue = getStaticValue(staticOption);
            let staticTitle = staticOption[props.staticTitleProp];
            const staticSubTitle = props.staticSubTitleProp ? staticOption[props.staticSubTitleProp] : "";

            let selectedOption: any = undefined;

            _.each(props.selectableOptions, (selectableOption: any) => {
                let key = props.selectableValueProp;

                if (props.selectedValueProp) {
                    key = props.selectableValueProp;
                }

                const selectableValue = selectableOption[key];

                if (selectableValue === staticValue) {
                    selectedOption = selectableOption;

                    staticTitle = selectableOption[props.selectableTitleProp];
                }
            });

            return {
                staticValue,
                staticTitle,
                staticSubTitle,
                selectedOption,
            } as IDataMapperProcessedItem;
        });

        setProcessedStaticOptions(_processedStaticOptions);
        setIsLoaded(true);
    };

    const closeModal = () => {
        props.cancelFn();
    };

    const saveData = () => {
        const dataMapperArr = processedStaticOptions.map((option) => {
            return {
                selectedItem: option.selectedOption,
                staticTitle: option.staticTitle,
                staticValue: option.staticValue,
            } as IDataMapperSaveItem;
        });

        props.saveFn(dataMapperArr);
    };

    const handleSelectedItem = (selectedItem: any, index: number) => {
        const _processedStaticOptions = { ...processedStaticOptions };

        _processedStaticOptions[index].selectedOption = selectedItem;

        setProcessedStaticOptions(_processedStaticOptions);
    };

    return (
        <div>
            <Modal
                isOpen={props.isVisible && isLoaded}
                onRequestClose={closeModal}
                contentLabel="Data Mapper"
                style={customStyles}
                overlayClassName={"modalOverlayClass"}
                className={"modalContentClass OwDataMapper"}>
                {/* HEADER */}
                <div className={"row"}>
                    <div className="col-12 d-flex">
                        <OwTitle title={props.mainTitle} isSupplierVendorOverride={isVendorNameOverride} supplierModel={supplierModel} />
                        <div className={"ms-auto"}>
                            <OwButton centerIconClass={"fas fa-times"} className={"btn-danger"} fn={closeModal} />
                        </div>
                    </div>
                </div>

                {/* MAIN CONTENT */}
                <div className={"titleRow"}>
                    <div className={"row"}>
                        <div className={"col-6"}>
                            <div>
                                <OwTitle
                                    title={props.staticTitle}
                                    tag={"h3"}
                                    isSupplierVendorOverride={isVendorNameOverride}
                                    supplierModel={supplierModel}
                                />
                            </div>
                        </div>

                        <div className={"col-6"}>
                            <div>
                                <OwTitle title={props.selectableTitle} tag={"h3"} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"mainContent"}>
                    <div className={"optionRows"}>
                        {processedStaticOptions.map((option: IDataMapperProcessedItem, index: number) => {
                            // const value = option.staticValue;
                            const title = option.staticTitle;
                            const subTitle = option.staticSubTitle;

                            const selectedOption = option.selectedOption;

                            return (
                                <div className={"row h-4r justify-content-center "} key={index}>
                                    {/* STATIC SIDE */}
                                    <div className={"col-6 d-flex flex-column static-side"}>
                                        <div>{title}</div>
                                        <div className={"text-muted"} style={{ wordBreak: "break-word" }}>
                                            {subTitle}
                                        </div>
                                    </div>

                                    {/* SELECTABLE SIDE */}
                                    <div className={"col-6 d-flex flex-column selectable-side"}>
                                        <div>
                                            <OwStaticTypeahead
                                                isSelectableFn={() => {
                                                    return true;
                                                }}
                                                selectedItem={selectedOption}
                                                selectedItemValueProp={props.selectableValueProp}
                                                selectedItemFn={(item: any) => {
                                                    handleSelectedItem(item, index);
                                                }}
                                                selectedItemTitleProp={props.selectableTitleProp}
                                                titleProp={props.selectableTitleProp}
                                                valueProp={props.selectableValueProp}
                                                typeaheadItems={props.selectableOptions}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* FOOTER */}
                <div className={"footer"}>
                    <OwButton title={"save"} fn={saveData} />
                </div>
            </Modal>
        </div>
    );
};
